import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';
import { reviewRequest, updateRequest, type RequestPatch, type RequestRow, type ReviewDecision } from './api';

/**
 * Writing to a request, from anywhere.
 *
 * Both hooks exist so that the three caches a request write can affect are
 * refreshed in one place rather than at each call site:
 *
 *   request(id)     the screen you are on
 *   requests        the Posts list, where the title and status are shown
 *   reviewQueue     the Review inbox, which a confirmation empties
 *
 * Forgetting the third is the interesting one: confirming a request from its
 * detail screen would leave it sitting in the inbox until a refetch, and the
 * next person would review it again.
 */

function useRequestWriteCaches(workspaceId: string, requestId: string) {
  const queryClient = useQueryClient();

  return (fresh: RequestRow) => {
    // The server's row, written straight into the detail cache: it is the
    // authority on derived fields (size from hours, status from board column)
    // and this avoids a refetch just to learn what we were already told.
    queryClient.setQueryData(queryKeys.request(workspaceId, requestId), fresh);
    void queryClient.invalidateQueries({ queryKey: queryKeys.requests(workspaceId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.reviewQueue(workspaceId) });
  };
}

export function useUpdateRequest(
  workspaceId: string,
  requestId: string,
): UseMutationResult<RequestRow, Error, RequestPatch> {
  const settle = useRequestWriteCaches(workspaceId, requestId);

  return useMutation({
    mutationFn: (patch: RequestPatch) => updateRequest(workspaceId, requestId, patch),
    onSuccess: settle,
  });
}

export function useReviewRequest(
  workspaceId: string,
  requestId: string,
): UseMutationResult<RequestRow, Error, ReviewDecision> {
  const settle = useRequestWriteCaches(workspaceId, requestId);

  return useMutation({
    mutationFn: (decision: ReviewDecision) => reviewRequest(workspaceId, requestId, decision),
    onSuccess: settle,
  });
}
